import type { JSX } from 'preact';
import { IdeasPage } from './pages/Ideas';
import { currentRoute, legacyRedirect } from './lib/router';
import { Masthead } from './components/Masthead';
import { PageShell } from './pages/PageShell';
import { PeoplePage } from './pages/People';
import { CompanyPage } from './pages/Company';
import { MyDeskPage } from './pages/MyDesk';
import { DriveSetupPage } from './pages/DriveSetup';
import { TasksPage } from './pages/Tasks';
import { HomePage } from './pages/Home';
import { LoginsPage } from './pages/Logins';
import { PayrollPage } from './pages/Payroll';
import { RecruitingPage } from './pages/Recruiting';
import { FaultState } from './components/FaultState';
import type { Role } from './lib/nav';

// Routes still served by the Phase 1 placeholder shell — a header and a
// named empty-state (SPEC-001 sequence: these pages become real in later
// phases). People, Company and My Desk graduated out of this table once
// their real pages existed and F-44 (component-local refetch on remount)
// was fixed — see People.tsx / Company.tsx / MyDesk.tsx and lib/store.ts.
const SHELL_ROUTES: Record<string, { title: string; emptyLabel: string }> = {


};

export function App({ role }: { role: Role }): JSX.Element {
  const path = currentRoute.value.path;

  let content: JSX.Element;
  if (path === '/people') {
    content = <PeoplePage />;
  } else if (path === '/company') {
    content = <CompanyPage />;
  } else if (path === '/my-desk') {
    content = <MyDeskPage />;
  } else if (path === '/drive-setup') {
    // Setup tool, not a daily page — deliberately reachable only by direct
    // path, never added to primary nav (lib/nav.ts). OWNER/DIRECTOR only,
    // gated inside DriveSetupPage itself.
    content = <DriveSetupPage />;
  } else if (path === '/ideas') {
    // The Keep mark in the nav opens THIS, not keep.google.com (owner,
    // 2026-08-17). Not in primary nav — the icon is its doorway.
    content = <IdeasPage />;
  } else if (path === '/tasks') {
    // Graduated out of the placeholder table 2026-08-15, together with
    // /reports: both pages were finished and tested but unreachable, because
    // nothing filled the store they read from. lib/bootstrap.ts does that now.
    content = <TasksPage />;
  } else if (path === '/money' || path === '/reports') {
    // Money and Reports are Company tabs now (owner, 2026-10-06). The router
    // already rewrites these addresses (lib/router.ts legacyRedirect); this
    // is the backstop for a route set without it.
    const moved = legacyRedirect(path, typeof window !== 'undefined' ? window.location.hash : '');
    if (moved && typeof window !== 'undefined') window.history.replaceState({}, '', moved);
    content = <CompanyPage />;
  } else if (path === '/') {
    content = <HomePage />;
  } else if (path === '/links') {
    content = <LoginsPage />;
  } else if (path === '/guide') {
    // The old Guide link (2026-10-06) now lands on nvRecruiter's Guide tab.
    if (typeof window !== 'undefined') window.history.replaceState({}, '', '/recruiting?tab=guide');
    content = <RecruitingPage />;
  } else if (path === '/payroll') {
    content = <PayrollPage />;
  } else if (path === '/recruiting') {
    // Its own top-line page since 2026-09-25 (owner); owner preview inside.
    content = <RecruitingPage />;
  } else if (SHELL_ROUTES[path]) {
    const page = SHELL_ROUTES[path];
    content = <PageShell title={page.title} emptyLabel={page.emptyLabel} />;
  } else {
    content = (
      <div class="main">
        <FaultState name="unknown route" detail={`No page is registered for "${path}".`} />
      </div>
    );
  }

  return (
    <div class="layout">
      <Masthead role={role} />
      {content}
    </div>
  );
}
